import React, {Component} from 'react'
import PropTypes from 'prop-types'
import {Modal, ModalBody, ModalFooter, ModalHeader} from "reactstrap"
import {message} from "antd"
import {blobToText, fileErrorMessage, importLayerConfig} from '../../../services/api/apiSku'
import {downloadBlob, exportStamp} from '../../ProductionPlanning/helpers/download'

/** Trần cỡ file, giữ đúng con số mà nút Import giá vốn trên cùng màn đang dùng (30MB). */
const MAX_FILE_SIZE = 31457280

/** Cỡ file cho người đọc. Luôn một chữ số thập phân để dòng không co giãn khi đổi file. */
const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`

    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`

    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

class ImportLayerConfigModal extends Component {
    state = {
        error: '',
        loading: false,
        file: null,
    }

    /*
     * Modal này bị component CHA unmount (`{isImportOpen && <ImportLayerConfigModal/>}`), nên
     * một request đang bay có thể trả về khi component đã biến mất. React 16 bỏ qua `setState`
     * trên component đã unmount VÀ không chạy callback của nó — nên mọi thứ phải-xảy-ra không
     * được đặt trong callback đó.
     */
    componentWillUnmount() {
        this._unmounted = true
    }

    _safeSetState = (state) => {
        if (this._unmounted) return

        this.setState(state)
    }

    /**
     * Báo lỗi cho người dùng bất kể modal còn sống hay không.
     *
     * Còn sống thì hiện dòng đỏ trong modal (người dùng đang nhìn vào đó). Đã đóng giữa chừng
     * thì phải đẩy ra toast, không thì lời giải thích biến mất hoàn toàn.
     */
    _reportError = (text) => {
        if (this._unmounted) {
            message.error(text)
            return
        }

        this.setState({loading: false, error: text})
    }

    _handleChangeFile = (e) => {
        const {files} = e.target

        // FileList rỗng khi người dùng mở hộp thoại rồi bấm Huỷ — đọc thẳng files[0].size ở đây
        // sẽ ném TypeError.
        if (!files || !files.length) {
            return this.setState({file: null})
        }

        const file = files[0]

        /*
         * Xoá value của input NGAY sau khi giữ được File.
         *
         * Trình duyệt không bắn `change` khi người dùng chọn lại đúng đường dẫn cũ. Mà đó lại là
         * ca thường gặp nhất ở màn này: nạp lỗi → tải file kết quả → sửa CHÍNH file vừa nạp →
         * chọn lại. Không reset thì lần chọn thứ hai im lặng không có tác dụng, và nút Nhập file
         * gửi lại đúng File cũ.
         */
        e.target.value = ''

        if (file.size > MAX_FILE_SIZE) {
            return this.setState({
                file: null,
                error: 'File vượt quá 30MB',
            })
        }

        this.setState({error: '', file})
    }

    _handleSubmit = (e) => {
        e.preventDefault()
        this._onSubmit()
    }

    _onSubmit = async () => {
        const {onImported, onToggleModal} = this.props
        const {file, loading} = this.state

        if (loading) return

        if (!file) {
            return this.setState({error: 'Vui lòng chọn file cần nhập'})
        }

        this.setState({loading: true, error: ''})

        try {
            const response = await importLayerConfig(file)

            /*
             * KIỂM STATUS TRƯỚC — bắt buộc, và phải đứng TRƯỚC nhánh content-type.
             *
             * `importLayerConfig` đi qua `requestFile`, vốn đặt `validateStatus: () => true` nên
             * axios không reject. Một cú 403 cũng mang `content-type: application/json` và sẽ lọt
             * thẳng vào nhánh "thành công" bên dưới, báo đã nhập xong trong khi thực tế bị từ chối.
             */
            if (response.status >= 400) {
                return this._reportError(await fileErrorMessage(response) || 'Không nhập được file. Vui lòng thử lại')
            }

            const contentType = `${response.headers['content-type'] || ''}`.toLowerCase()

            /*
             * Phân loại bằng Content-Type, KHÔNG bằng Content-Disposition.
             *
             * Content-Type là header CORS-safelisted nên trình duyệt luôn cho đọc.
             * Content-Disposition thì không — muốn đọc phải có `Access-Control-Expose-Headers` từ
             * BE. Vì vậy tên file tải xuống do màn này tự dựng.
             *
             * Cũng không phân loại bằng cách "thử JSON.parse, lỗi thì coi là CSV": một file CSV chỉ
             * có cột Lỗi với nội dung là số cũng parse ra JSON hợp lệ.
             */
            if (contentType.includes('application/json')) {
                return await this._onSuccess(response, onImported, onToggleModal)
            }

            /*
             * Có ít nhất một dòng lỗi ⇒ all-or-nothing: CHƯA SKU NÀO ĐƯỢC GHI.
             *
             * Giữ modal mở là có chủ đích — đóng modal sau khi trả file lỗi là ngụ ý "xong việc",
             * trong khi người dùng còn phải sửa file rồi nạp lại.
             */
            /*
             * Chỉ coi là "file kết quả lỗi" khi BE thật sự trả một file văn bản.
             *
             * Phản hồi 2xx thiếu Content-Type hoặc mang kiểu lạ KHÔNG được đoán là file lỗi: báo
             * "chưa có SKU nào được ghi" trong khi BE đã ghi xong là mời người dùng nạp lại một
             * thao tác ghi đè. Thà nói không nhận dạng được còn hơn khẳng định sai.
             *
             * ⚠️ KHÔNG nới thành `text/` cho "an toàn": `text/html` cũng khớp tiền tố đó, nên trang
             *    lỗi HTML của gateway sẽ bị tải xuống thành `*-error-*.csv` kèm câu khẳng định sai
             *    là chưa ghi gì. `res.attachment()` của express đặt Content-Type theo đuôi file
             *    (`.csv` → `text/csv`), nên chỉ cần khớp `csv`; `octet-stream` giữ cho trường hợp
             *    proxy tước mất kiểu cụ thể.
             *
             * ⚠️ Chờ T0 chốt Content-Type thật của nhánh file lỗi.
             */
            if (!contentType.includes('csv') && !contentType.includes('octet-stream')) {
                return this._reportError('Phản hồi không nhận dạng được từ hệ thống. Dùng Export để kiểm tra lại trước khi nạp lần nữa')
            }

            downloadBlob(response.data, `sku-layer-config-error-${exportStamp()}.csv`)

            /*
             * `downloadBlob` là side effect DOM, chạy bất kể modal còn sống hay không — nên lời
             * giải thích phải đi cùng đường. Đóng modal giữa chừng mà chỉ `setState` thì người
             * dùng nhận một file .csv lạ trong Downloads, không biết vì sao và không biết là
             * CHƯA có SKU nào được ghi.
             */
            this._reportError('File có dòng lỗi — chưa có SKU nào được ghi. Đã tải file kết quả kèm cột Lỗi, vui lòng sửa rồi nạp lại')
        } catch (e) {
            // validateStatus nuốt mọi status, nên tới đây chỉ còn lỗi mạng / request bị huỷ.
            this._reportError('Không nhập được file. Vui lòng thử lại')
        }
    }

    _onSuccess = async (response, onImported, onToggleModal) => {
        let body = null

        try {
            body = JSON.parse(await blobToText(response.data))
        } catch (e) {
            body = null
        }

        // Envelope của service là {success, data, message}. Status 200 kèm success=false là ca
        // hiếm nhưng có thật trong repo, nên không mặc định coi 200 là đã ghi xong.
        if (body && body.success === false) {
            return this._reportError(body.message || 'Không nhập được file. Vui lòng thử lại')
        }

        /*
         * Số SKU đã ghi nằm ở `data.imported` — BE `importLayerConfig` trả `{imported}`, `sendSuccess`
         * bọc thành `{success, data: {imported}}` (docs/layer-config-contract.md bên fulfillment-service).
         * Con số này là cơ chế đối chiếu DUY NHẤT của màn, vì bảng SKU không có cột layer nào để
         * người dùng nhìn thấy thay đổi. Đọc sai khoá thì nhánh fallback dưới đây chạy và trông
         * hoàn toàn bình thường — nên đừng đổi khoá mà không đối chiếu contract.
         */
        const imported = body && body.data && body.data.imported

        /*
         * Ba tác dụng dưới đây KHÔNG được đặt trong callback của `setState`.
         *
         * React 16 bỏ qua `setState` trên component đã unmount và KHÔNG chạy callback của nó.
         * Modal này bị cha unmount khi người dùng bấm Hủy / ESC / nền, nên đặt trong callback
         * là: BE đã ghi đè xong mà không có thông báo, bảng SKU không nạp lại, người dùng tưởng
         * thất bại rồi nạp lại file lần nữa — trên một thao tác ghi đè destructive.
         *
         * `message` của antd là global, và `onImported` thuộc component CHA (vẫn đang sống), nên
         * hai cái đó chạy VÔ ĐIỀU KIỆN. Chỉ `setState` và `onToggleModal` mới cần guard unmount.
         */
        // Nhắc ĐÚNG nhãn đang hiển thị trên màn ("Cấu trúc layer" → "Export cấu trúc"), khớp
        // từng chữ với dòng hướng dẫn trong modal. Không có control nào tên "Xuất".
        message.success(
            typeof imported === 'number'
                ? `Đã nhập cấu trúc layer cho ${imported} SKU. Bấm Cấu trúc layer → Export cấu trúc để đối chiếu lại`
                : 'Đã nhập cấu trúc layer. Bấm Cấu trúc layer → Export cấu trúc để đối chiếu lại'
        )
        onImported()

        this._safeSetState({loading: false})

        if (!this._unmounted) onToggleModal()
    }

    render() {
        const {isOpen, onToggleModal} = this.props
        const {file, loading, error} = this.state

        return (
            <div className="ModalImportLayerConfig">
                {/*
                  * Khi đang ghi thì khoá mọi đường đóng modal: nền tĩnh, ESC vô hiệu, ẩn dấu X.
                  * Import là thao tác ghi đè destructive — đóng giữa chừng không huỷ được request
                  * đã bay, chỉ làm người dùng mất dấu kết quả. Logic bên trên đã chịu được unmount,
                  * đây là lớp thứ hai để tình huống đó đừng xảy ra ngay từ đầu.
                  */}
                <Modal
                    isOpen={isOpen}
                    toggle={onToggleModal}
                    backdrop={loading ? 'static' : true}
                    keyboard={!loading}
                    /*
                     * Class phải đi qua `modalClassName`, KHÔNG dựa vào div bọc bên ngoài:
                     * reactstrap Modal tự tạo một div rồi document.body.appendChild — nội dung
                     * modal nằm ngoài cây con của `.ModalImportLayerConfig` ở trên, nên selector
                     * lồng theo div đó không bao giờ khớp.
                     */
                    modalClassName="ModalImportLayerConfig"
                >
                    <ModalHeader toggle={loading ? undefined : onToggleModal}>Nhập cấu trúc layer</ModalHeader>
                    <ModalBody>
                        <div className="ImportTracking">
                            <div className="AttachFile">
                                <input
                                    id="import_layer_config_file"
                                    type="file"
                                    accept=".csv"
                                    multiple={false}
                                    onChange={this._handleChangeFile}
                                />
                                {/*
                                  * Nhãn đổi hẳn trạng thái khi đã có file: viền xanh liền thay cho
                                  * viền đứt, và chữ chuyển thành "Chọn file khác" để vẫn thấy đây
                                  * là chỗ bấm chọn lại — nếu chỉ đổi màu mà giữ nguyên chữ "Chọn
                                  * file cần import" thì người dùng tưởng file chưa vào.
                                  */}
                                <label
                                    htmlFor="import_layer_config_file"
                                    className={`d-block mb-0 cursor-pointer${file ? ' has-file' : ''}`}
                                >
                                    <i className={`fa ${file ? 'fa-check-circle' : 'fa-cloud-upload'}`}/>{' '}
                                    {file ? 'Chọn file khác' : 'Chọn file cần import'}
                                </label>
                            </div>
                            {/*
                              * Import ở đây ghi đè destructive, nên file sắp nạp phải đập vào mắt
                              * trước khi bấm Nhập file. Kèm cỡ file để người dùng phát hiện sớm
                              * trường hợp chọn nhầm file rỗng hoặc nhầm file khác cùng tên.
                              */}
                            {file && <div className="ListFileInput">
                                <ul>
                                    <li className="FileSelected">
                                        <div className="Left">
                                            <i className="fa fa-paperclip"/>
                                            <span className="FileName">{file.name}</span>
                                            <span className="FileSize">{formatFileSize(file.size)}</span>
                                        </div>
                                    </li>
                                </ul>
                            </div>}
                        </div>

                        <div className="alert alert-warning mt-3 mb-0">
                            <div><strong>Ghi đè toàn bộ.</strong> Cấu trúc layer hiện có của các SKU trong
                                file sẽ bị thay thế, không phải bổ sung.</div>
                            <div className="mt-1">SKU để trống toàn bộ cột layer sẽ bị <strong>xoá hết
                                layer</strong> đang có.</div>
                            <div className="mt-1">SKU không xuất hiện trong file thì không bị ảnh hưởng.</div>
                            <div className="mt-1">Một dòng sai là <strong>không SKU nào</strong> được ghi —
                                hệ thống trả về file kết quả kèm cột Lỗi.</div>
                        </div>

                        <div className="mt-3 text-muted">
                            <div>Lấy file để điền bằng <strong>Cấu trúc layer → Export cấu trúc</strong> — file
                                xuất ra đã có sẵn đúng mã SKU theo bộ lọc đang áp dụng.</div>
                            <div className="mt-1">
                                <span role="img" aria-label="Cảnh báo">⚠️</span>{' '}
                                Mở file bằng Excel có thể làm mất số 0 đầu của mã NVL
                                (<code>0203</code> → <code>203</code>). Định dạng các cột layer là
                                <strong> Text</strong> trước khi điền.</div>
                        </div>

                        {error && <div className="text-danger mt-3">{error}</div>}
                    </ModalBody>
                    <ModalFooter className="d-flex justify-content-between">
                        <button disabled={loading} className="btn btn-secondary" onClick={onToggleModal}>Hủy</button>
                        <button disabled={loading} onClick={this._handleSubmit} className="btn btn-primary">
                            {loading && <i className='fa fa-spin fa-spinner mr-2'/>} Nhập file
                        </button>
                    </ModalFooter>
                </Modal>
            </div>
        )
    }
}

ImportLayerConfigModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onToggleModal: PropTypes.func.isRequired,
    onImported: PropTypes.func.isRequired,
}

export default ImportLayerConfigModal
